import type { Message } from '../lib/api'
import { PartsView } from './MessageParts'

/**
 * BroadcastView renders a broadcast conversation as rounds of columns — one
 * prompt, N agents answering it side by side — rather than the linear bubble
 * feed a direct or group chat uses.
 *
 * A linear feed is the wrong shape for this: the whole point of a broadcast is
 * comparing answers, and interleaving three agents' replies to the same
 * question into one scrolling column makes them harder to compare, not
 * easier. Columns on a wide screen, stacked cards on a narrow one — the same
 * idiom a spreadsheet or a diff view uses for "here are several versions of
 * the same thing, side by side".
 */

/** Round is one prompt and every agent's reply to it. */
interface Round {
  prompt: Message
  replies: Message[]
}

/**
 * groupIntoRounds splits a broadcast's messages into rounds.
 *
 * Every agent reply from one Send call is created immediately after its
 * triggering user message and before the next one — Manager.Send allocates
 * all of a round's rows in one pass — so a user message always starts a new
 * round and every agent message up to the next user message belongs to it.
 * No explicit "which round is this" column is needed on the schema; sequence
 * order already encodes it.
 */
function groupIntoRounds(messages: Message[]): Round[] {
  const rounds: Round[] = []
  for (const m of messages) {
    if (m.role === 'user') {
      rounds.push({ prompt: m, replies: [] })
      continue
    }
    if (m.role === 'agent' && rounds.length > 0) {
      rounds[rounds.length - 1].replies.push(m)
    }
  }
  return rounds
}

function ReplyColumn({ message, senderName }: { message: Message; senderName: string }) {
  const failed = message.status === 'failed'
  return (
    <div
      className="min-w-[85vw] flex-none overflow-hidden rounded-lg border bg-card md:min-w-0 md:max-w-sm md:flex-1"
      data-testid="broadcast-column"
    >
      <div className="border-b bg-muted/40 px-3 py-2">
        <p className="text-sm font-semibold">{senderName}</p>
      </div>
      <div className="px-3 py-3 text-sm">
        <PartsView parts={message.parts ?? []} />
        {(message.parts ?? []).length === 0 && !failed && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="size-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
            <span className="text-xs">typing…</span>
          </div>
        )}
      </div>
    </div>
  )
}

export function BroadcastView({
  messages,
  senderName,
}: {
  messages: Message[]
  senderName: (m: Message) => string
}) {
  const rounds = groupIntoRounds(messages)

  return (
    <div className="flex flex-col gap-6 px-4">
      {rounds.map((round) => (
        <div key={round.prompt.id}>
          <div className="mb-3 flex justify-center">
            <div className="max-w-[90%] rounded-full bg-primary/10 px-4 py-2 text-center text-sm font-medium text-primary md:max-w-[70%]">
              {round.prompt.parts?.find((p) => p.kind === 'text')?.text ?? ''}
            </div>
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2 md:flex-wrap md:overflow-x-visible md:pb-0">
            {round.replies.map((r) => (
              <ReplyColumn key={r.id} message={r} senderName={senderName(r)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
